import React, { useEffect, useState, useCallback } from "react";
import CollapsibleCard from "../components/ui/CollapsibleCard";
import { toParisTime } from "../utils/date";
import { apiFetch } from "../utils/api";

// Écran de consultation POK — spec verrouillée le 11/09/2026 (cf. docs/tasks/PLAN_DE_TRAVAIL.md) :
// campagne (bucket N/A en repli) -> table plate d'essais (pas de niveau "site" comme Syrinix, un
// essai POK est déjà un point unique). Colonnes et actions toutes tirées de results.json, aucun
// calcul serveur.

function ClassificationBadge({ value }) {
  if (!value) return <span className="text-gray-400">—</span>;
  const color = value === "conforme"
    ? "bg-green-100 text-green-800 border-green-300"
    : value === "non conforme"
    ? "bg-red-100 text-red-800 border-red-300"
    : "bg-gray-100 text-gray-500 border-gray-300";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${color}`}>
      {value}
    </span>
  );
}

function fmtNumber(v) {
  return v == null ? "—" : v.toFixed(2);
}

function Lightbox({ src, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <img src={src} alt="Capture d'écran de l'essai" className="max-w-full max-h-full rounded shadow-lg" />
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white text-2xl leading-none hover:text-gray-300"
        aria-label="Fermer"
      >
        ✕
      </button>
    </div>
  );
}

function EssaiRow({ essai, onRetry, retryState, onOpenPhoto }) {
  return (
    <tr className="bg-white border-b hover:brightness-95">
      <td className="px-3 py-2 font-mono font-medium">{essai.pi_id || "—"}</td>
      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{toParisTime(essai.timestamp_start)}</td>
      <td className="px-3 py-2"><ClassificationBadge value={essai.classification} /></td>
      <td className="px-3 py-2 whitespace-nowrap">{fmtNumber(essai.p_at_q60_bar)} bar</td>
      <td className="px-3 py-2 whitespace-nowrap">{fmtNumber(essai.q_at_p1_m3h)} m³/h</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1 flex-wrap">
          <a
            href={`/api/pok/essais/${essai.id}/raw.csv`}
            className="inline-flex items-center px-2 py-0.5 text-xs rounded border border-gray-400 text-gray-600 hover:bg-gray-50"
            download
          >
            CSV
          </a>
          <button
            type="button"
            onClick={() => onOpenPhoto(`/api/pok/essais/${essai.id}/screenshot`)}
            title="Voir la capture d'écran de l'essai"
            className="inline-flex items-center px-2 py-0.5 text-xs rounded border border-gray-400 text-gray-600 hover:bg-gray-50"
          >
            📷 Photo
          </button>
          <button
            type="button"
            onClick={() => {
              const url = new URL("/visualisation/POK", window.location.origin);
              url.searchParams.set("date", essai.date);
              url.searchParams.set("essai_folder", essai.essai_folder);
              window.open(url.toString(), "_blank", "noopener,noreferrer");
            }}
            className="inline-flex items-center px-2 py-0.5 text-xs rounded border border-violet-500 text-violet-600 hover:bg-violet-50"
          >
            Streamlit
          </button>
          <button
            type="button"
            onClick={() => onRetry(essai)}
            disabled={retryState === "loading"}
            title="Réessayer l'association de campagne"
            className={`inline-flex items-center px-2 py-0.5 text-xs rounded border whitespace-nowrap transition-colors disabled:opacity-50 ${
              retryState === "ok"
                ? "border-green-500 text-green-600 bg-green-50"
                : retryState === "err"
                ? "border-red-400 text-red-600 bg-red-50"
                : "border-gray-400 text-gray-500 hover:bg-gray-50"
            }`}
          >
            {retryState === "loading" ? "..." : retryState === "ok" ? "✓" : retryState === "err" ? "Erreur" : "↺ Retry"}
          </button>
        </div>
      </td>
    </tr>
  );
}

function CampagneSection({ campagneId, nom, essais, onRetry, retryStates, onOpenPhoto }) {
  return (
    <CollapsibleCard
      icon={nom === "N/A" ? "❓" : "📋"}
      title={nom}
      badge={`${essais.length} essai${essais.length !== 1 ? "s" : ""}`}
      headerRight={
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            const url = new URL("/rapport-triage-pok", window.location.origin);
            url.searchParams.set("campagne_id", campagneId);
            window.open(url.toString(), "_blank", "noopener,noreferrer");
          }}
          className="inline-flex items-center px-2 py-0.5 text-xs rounded border border-violet-500 text-violet-600 hover:bg-violet-50"
        >
          Rapport de triage
        </button>
      }
      defaultOpen={false}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-100 text-gray-600 uppercase tracking-wide">
              <th className="px-3 py-2 text-left border-b font-medium">PEI</th>
              <th className="px-3 py-2 text-left border-b font-medium whitespace-nowrap">Date / heure</th>
              <th className="px-3 py-2 text-left border-b font-medium">Classification</th>
              <th className="px-3 py-2 text-left border-b font-medium whitespace-nowrap">P à Q60</th>
              <th className="px-3 py-2 text-left border-b font-medium whitespace-nowrap">Q à 1 bar</th>
              <th className="px-3 py-2 text-left border-b font-medium">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {essais.map((essai) => (
              <EssaiRow
                key={essai.id}
                essai={essai}
                onRetry={onRetry}
                retryState={retryStates[essai.id]}
                onOpenPhoto={onOpenPhoto}
              />
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleCard>
  );
}

export default function PokEssais() {
  const [essais, setEssais] = useState(null);
  const [error, setError] = useState(null);
  const [retryStates, setRetryStates] = useState({}); // { [essai.id]: 'loading'|'ok'|'err' }
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const loadEssais = useCallback(async () => {
    try {
      setEssais(await apiFetch("/api/pok/essais"));
      setError(null);
    } catch (err) {
      setError(err.message || "Erreur de chargement");
      setEssais([]);
    }
  }, []);

  useEffect(() => { loadEssais(); }, [loadEssais]);

  const handleRetry = async (essai) => {
    setRetryStates(prev => ({ ...prev, [essai.id]: "loading" }));
    try {
      const { campagne } = await apiFetch(`/api/pok/essais/${essai.id}/resolve-campaign`, { method: "POST" });
      setEssais(prev => prev.map(e => e.id === essai.id ? { ...e, campagne } : e));
      setRetryStates(prev => ({ ...prev, [essai.id]: "ok" }));
    } catch {
      setRetryStates(prev => ({ ...prev, [essai.id]: "err" }));
    } finally {
      setTimeout(() => setRetryStates(prev => ({ ...prev, [essai.id]: undefined })), 3000);
    }
  };

  if (essais === null) {
    return <div className="card p-6 text-sm text-gray-400">Chargement...</div>;
  }

  if (error) {
    return (
      <div className="card p-4 bg-red-50 border-red-200">
        <p className="text-red-800 text-sm">❌ {error}</p>
      </div>
    );
  }

  if (essais.length === 0) {
    return (
      <div className="card p-4 bg-yellow-50 border-yellow-200 text-sm text-yellow-800">
        Aucun essai POK synchronisé pour l'instant.
      </div>
    );
  }

  // Regroupé par id de campagne, pas par nom (form_id) : "NA" (sans "/", cf. backend
  // pok_triage.py::NA_CAMPAGNE_ID) est le bucket des essais non associés — utilisé aussi comme
  // campagne_id du rapport de triage, donc groupé par id plutôt que par nom pour rester
  // cohérent même si deux campagnes portaient un jour le même nom.
  const byCampagne = {};
  for (const essai of essais) {
    const id = essai.campagne?.form_id || "NA";
    const nom = essai.campagne?.nom || "N/A";
    (byCampagne[id] ??= { nom, essais: [] }).essais.push(essai);
  }
  const campagneIds = Object.keys(byCampagne).sort((a, b) => {
    if (a === "NA") return 1;
    if (b === "NA") return -1;
    return byCampagne[a].nom.localeCompare(byCampagne[b].nom);
  });

  return (
    <div className="space-y-4">
      {campagneIds.map(id => (
        <CampagneSection
          key={id}
          campagneId={id}
          nom={byCampagne[id].nom}
          essais={byCampagne[id].essais}
          onRetry={handleRetry}
          retryStates={retryStates}
          onOpenPhoto={setLightboxSrc}
        />
      ))}
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}
